import {FC, RefObject, useMemo} from 'react';
import {Box, Flex, Grid} from '@chakra-ui/react';
import {EOAuthType, EServiceGroup, IIntegration, useT} from '@kcast-fe/core';

import {AccountItem, NoAccounts, NewAccountMenu} from './components';

interface IProps {
  hasAccounts: boolean;
  integrations: IIntegration[];
  ref: RefObject<HTMLElement | null>;
  onOpenCredentialsForm: (type: EOAuthType) => void;
}

export const GroupedAccountList: FC<IProps> = ({
  ref,
  hasAccounts,
  integrations,
  onOpenCredentialsForm
}) => {
  const {t} = useT();

  const items = useMemo(() => {
    const groups = new Map<EServiceGroup, IIntegration[]>();
    for (const integration of integrations) {
      if (integration.accounts.length > 0) {
        const group = groups.get(integration.service_group);
        if (group) {
          group.push(integration);
        } else {
          groups.set(integration.service_group, [integration]);
        }
      }
    }
    return groups;
  }, [integrations]);

  return (
    <>
      {/* NO CONNECTIONS */}
      {!hasAccounts && <NoAccounts />}
      {/* CONNECTION LIST*/}
      <Box p={'0 24px'} minH={'1px'} flex={'1 1 auto'} overflowY={'auto'}>
        {Array.from(items.entries()).map(([serviceGroup, groupIntegrations]) => (
          <Flex key={serviceGroup} gap={'24px'} flexDirection={'column'}>
            <Box key={serviceGroup}>
              <Grid
                p={{base: '8px 0 0 0', smToMd: '8px 0'}}
                gridTemplateColumns={{base: '1fr 204px', smToMd: '1fr 106px'}}
              >
                <Box fontWeight={600}>{t(`enums.serviceGroup.${serviceGroup}`)}</Box>
                <Box>{t('common.default')}</Box>
              </Grid>

              {groupIntegrations.flatMap((integration) =>
                integration.accounts.map((account) => (
                  <AccountItem
                    key={account.platform_unique_id}
                    service={integration.service}
                    account={account}
                  />
                ))
              )}
            </Box>
          </Flex>
        ))}
      </Box>

      {/* NEW CONNECTION */}
      <Flex p={'0 24px'} justifySelf={'flex-end'} flexDirection={'column'}>
        <NewAccountMenu
          ref={ref}
          hasAccount={hasAccounts}
          integrations={integrations}
          onOpenCredentialsForm={onOpenCredentialsForm}
        />
      </Flex>
    </>
  );
};
